"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, FileCheck, ChartBar as BarChart3, Users, Calculator, ShieldCheck, TrendingUp, Fuel, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const serviceCategories = [{
  id: "contable",
  icon: BookOpen,
  title: "Registro y procesamiento contable",
  description: "Captura sistemática de operaciones, conciliaciones bancarias y depuración analítica de cuentas conforme a las NIF."
}, {
  id: "fiscal",
  icon: FileCheck,
  title: "Declaraciones fiscales y cumplimiento",
  description: "Determinación y presentación oportuna de pagos provisionales, definitivos y anuales ante el SAT y entidades estatales."
}, {
  id: "financiero",
  icon: BarChart3,
  title: "Emisión y análisis de estados financieros",
  description: "Balances generales, estados de resultados y razones financieras interpretadas para la toma oportuna de decisiones."
}, {
  id: "nomina",
  icon: Users,
  title: "Maquila y administración de nómina",
  description: "Cálculo integral de percepciones, retenciones de ISR, cuotas obrero-patronales del IMSS, INFONAVIT y timbrado CFDI."
}, {
  id: "costos",
  icon: Calculator,
  title: "Controles administrativos, costos y presupuestos",
  description: "Estructuración de costos operativos, proyecciones presupuestales y tableros de control para blindar la rentabilidad."
}, {
  id: "control",
  icon: ShieldCheck,
  title: "Finanzas corporativas y control interno",
  description: "Diseño de manuales de políticas, arqueos, supervisión de flujos de efectivo y mitigación de riesgos operativos."
}, {
  id: "estrategica",
  icon: TrendingUp,
  title: "Consultoría empresarial estratégica",
  description: "Acompañamiento directivo en reestructuraciones corporativas, planes de expansión patrimonial y gobierno corporativo."
}, {
  id: "estaciones",
  icon: Fuel,
  title: "Atención especializada a estaciones de servicio",
  description: "Contabilidad sectorial y cumplimiento normativo ante CRE, ASEA y SAT para franquicias y grupos gasolineros."
}];
const highlights = ["Supervisión directa por contadores públicos certificados", "Reportes ejecutivos periódicos y accesibles en línea", "Alineación estricta a reformas fiscales y mercantiles"];
export default function Enterprise() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="servicios-integrales-para-empresas" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {/* Header Briefing */}
        <div className="mb-14 lg:mb-18">
          <div className="inline-flex items-center gap-2 rounded-xs border border-accent/40 bg-accent/30 px-3 py-1 text-xs font-semibold tracking-widest text-foreground uppercase">
            <span>Empresas</span>
            <span className="text-primary font-bold">·</span>
            <span>Servicios Integrales</span>
          </div>

          <h2 className="mt-4 text-3xl font-semibold tracking-tight uppercase sm:text-4xl lg:text-5xl text-foreground font-serif">
            Servicios integrales para empresas
          </h2>

          {/* ASV Gold Hairline motif */}
          <div className="mt-4 h-[2px] w-14 bg-ring" />

          <p className="mt-4 max-w-3xl text-base sm:text-lg text-muted-foreground font-sans">
            Soluciones de contabilidad, estrategia tributaria y control directivo
            diseñadas para blindar la operación y respaldar el crecimiento de su
            organización.
          </p>
        </div>

        {/* 2-Column Split: Visual Dossier (Left) & Service Cards Grid (Right) */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14 items-start">
          {/* Left Column: Visual Briefing Card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="relative overflow-hidden rounded-xs border border-border shadow-md bg-card">
              {/* Photo */}
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/corporate_accounting_team.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/corporate_accounting_team.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/corporate_accounting_team.webp-wvc-sizes" alt="Equipo de consultores y contadores analizando estados financieros corporativos" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-secondary/15 pointer-events-none" />
              </div>

              {/* Card Meta Dossier */}
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>Práctica Corporativa</span>
                  <span className="text-ring font-bold">·</span>
                  <span>ASV Consultores</span>
                </div>

                <h3 className="mt-2 text-xl font-semibold uppercase tracking-tight text-foreground font-serif">
                  Acompañamiento contable y fiscal continuo
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground font-sans">
                  Transformamos el cumplimiento obligatorio en una herramienta
                  de orden y rentabilidad para accionistas y directores generales.
                </p>

                <Separator className="my-5" />

                {/* Bullet Points */}
                <ul className="space-y-3">
                  {highlights.map((item, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-xs sm:text-sm text-foreground">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-ring" />
                      <span>{item}</span>
                    </li>)}
                </ul>

                {/* Call To Action */}
                <div className="mt-7 pt-2">
                  <Button asChild className="w-full bg-primary text-primary-foreground border border-ring font-medium tracking-wider uppercase text-xs hover:bg-ring hover:text-primary transition-colors">
                    <Link to="/ContactPage">
                      Solicitar asesoría empresarial
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Contact Micro-Card */}
            <div className="rounded-xs border border-border bg-card p-5 shadow-xs">
              <p className="text-xs uppercase tracking-wider font-semibold text-foreground font-serif">
                ¿Requiere cotización sectorial o diagnóstico preliminar?
              </p>
              <p className="mt-1 text-xs text-muted-foreground font-sans">
                Atención directa con nuestro equipo multidisciplinario en Los Mochis y en todo el país.
              </p>
              <div className="mt-3">
                <Button asChild variant="outline" size="sm" className="w-full border-border text-foreground hover:bg-muted text-xs tracking-wider uppercase">
                  <Link to="/OnlineConsultingPage">
                    Hablar con un especialista
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Comprehensive Thematic Cards Grid */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {serviceCategories.map((service, i) => {
            const IconComponent = service.icon;
            return <motion.div key={service.id} data-index={i} variants={itemVariants} className="h-full">
                  <Card className="h-full rounded-xs border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-ring/60 hover:shadow-md">
                    <CardHeader className="gap-3 pb-2 pt-6 px-6">
                      <div className="inline-flex size-10 items-center justify-center rounded-xs border border-ring/40 bg-accent/40 text-primary">
                        <IconComponent className="size-5 text-ring" />
                      </div>
                      <CardTitle className="text-base font-semibold leading-snug tracking-tight text-foreground font-serif uppercase">
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6 pt-1">
                      <p className="text-xs sm:text-sm leading-relaxed text-muted-foreground font-sans">
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}